// packages/widget/src/components/steps/ticket-coupon.ts
//
// #816 (#775-f) — "Have a promo code?" at ticket checkout.
//
// This component types a code and RENDERS NUMBERS IT WAS GIVEN. It does not
// multiply, does not apply a percentage, does not derive a total: `percentOff`
// is a label, `subtotalCents` / `discountCents` are the server's own money, and
// the total lives in the price row next door (also the server's). Any
// arithmetic here would be a second source of truth for a charge, which is
// exactly the class of bug the ticket rail is built to avoid.
//
// Three honesty rules it enforces:
//   1. An ABSENT `ticketsRemaining` is UNKNOWN, never "unlimited" — with no
//      number from the server there is no pool hint at all.
//   2. A refusal shows the SERVER's sentence verbatim; the widget adds only the
//      reassurance that the order is still purchasable — at full price, or
//      (#954) with the auto deal that still applies. Never "full price" while
//      a deal is on the order.
//   3. A refused code STAYS in the field — a typo is corrected, not retyped.
//
// #954 — it also draws the event's AUTO deal (e.g. Buy 1 Get 1 Free), which is
// code-free to the guest: "Buy 1 Get 1 Free applied (−R 500.00)" from the
// server's label + discount, or the server's own sentence when a live deal did
// not fit the basket (a nudge, the same-type explainer, "fully redeemed").
// Those lines, and the typed-code "applied" line, sit in ONE polite live
// region that is always in the DOM, so each change is announced once and a
// re-render that changes nothing announces nothing (AC-954-46).
//
// #954 a11y M1/M2 — that region must exist BEFORE its first words. This
// element mounts together with the first quote, so a region inside it would be
// inserted already populated (screen readers do not announce that). The host
// steps therefore render the region themselves, from their first paint, and
// set `external-status` here: the coupon then draws no status / refusal of its
// own (one speaker), and the host fills its region with
// `renderCouponStatusContent` — the same words and markup, one source.

import { LitElement, html, css, nothing, type PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { themeTokens, resetStyles, baseStyles } from '../../styles/tokens.css.js';

/**
 * The pool hint fires only when the server sent a number AND that number is
 * small enough to change a decision. A big pool is noise ("only 250 left" is
 * not urgency, it is clutter), and no number at all is silence — never a claim.
 */
export const COUPON_POOL_HINT_MAX = 10;

/**
 * #953 — what a 100%-comped total reads. Used verbatim by the service step and
 * the recurring occurrence picker so the two cannot drift.
 */
export const COMPED_TOTAL_COPY = 'Free (complimentary)';

/**
 * #953 — is this quote a server-accepted comp? All three inputs are the
 * SERVER's: an accepted code, a real pre-discount subtotal, and a zero total.
 * A zero total with no accepted code is NOT a comp — that stays blocked (#823
 * is a separate decision), and a typed-but-unaccepted code never counts.
 */
export function isCompedQuote(
  totalCents: number,
  subtotalCents: number,
  appliedCode: string,
  /** #954 — the server applied the event's auto deal (no typed code). */
  autoApplied = false,
): boolean {
  return (!!appliedCode || autoApplied) && totalCents === 0 && subtotalCents > 0;
}

/** Trim + uppercase. Same rule as the store's `normalizeCouponCode`. */
function normalize(raw: string): string {
  return String(raw ?? '').trim().toUpperCase();
}

/** The SERVER's cents, formatted — never computed. */
function formatCouponMoney(cents: number, currency: string): string {
  const major = cents / 100;
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: (currency || 'USD').toUpperCase(),
    }).format(major);
  } catch {
    return `${(currency || 'USD').toUpperCase()} ${major.toFixed(2)}`;
  }
}

/** What the coupon's live region says. Every input is the server's. */
export interface CouponStatusInput {
  appliedCode: string;
  dealApplied: boolean;
  dealLabel: string;
  dealMessage: string;
  discountCents: number;
  currency: string;
  /** The server's refusal sentence for a typed code ('' when none). */
  error: string;
}

/** #954 — the auto deal is on this order (and no typed code replaced it). */
function dealOn(p: CouponStatusInput): boolean {
  return p.dealApplied && !p.appliedCode && p.discountCents > 0;
}

/**
 * #954 — the server's not-applied sentence, shown only when it adds
 * something: not while a typed code is applied (the code replaced the deal),
 * and not when the refusal in the same region already says the same words
 * (the refusal is announced too, so nothing is lost — a11y M2).
 */
function dealHint(p: CouponStatusInput): string {
  const msg = String(p.dealMessage ?? '').trim();
  if (!msg || p.dealApplied || p.appliedCode) return '';
  if (p.error && p.error.includes(msg)) return '';
  return msg;
}

/**
 * AC-954-19 — the reassurance under a refused code must be TRUE: while the
 * auto deal still applies the order is not at "full price".
 */
function errorNote(p: CouponStatusInput): string {
  if (dealOn(p)) return `${String(p.dealLabel ?? '').trim() || 'Your discount'} still applies to your order.`;
  return 'You can still book at the full price.';
}

/**
 * The CONTENT of the coupon live region — applied / deal / nudge line, then
 * the refusal (a11y M2). The region itself is the caller's: a host step
 * renders it from first paint so this content arrives as a mutation inside an
 * existing region. Separate plain-text bindings, so Lit only touches the part
 * whose words changed (no re-announce on an unrelated re-render).
 */
export function renderCouponStatusContent(p: CouponStatusInput) {
  let line: unknown = nothing;
  if (p.appliedCode) {
    line = html`<div class="coupon-applied"><span>Promo code ${p.appliedCode} applied.</span></div>`;
  } else if (dealOn(p)) {
    line = html`<div class="deal-applied">${String(p.dealLabel ?? '').trim() || 'Discount'} applied (−${formatCouponMoney(p.discountCents, p.currency)})</div>`;
  } else {
    const hint = dealHint(p);
    if (hint) line = html`<p class="deal-hint">${hint}</p>`;
  }
  const refusal = p.error
    ? html`<div class="coupon-error">${p.error} <span class="coupon-error-note">${errorNote(p)}</span></div>`
    : nothing;
  return html`${line}${refusal}`;
}

/**
 * #954 a11y L2/L3 — the deal colour, and the styles for the status content.
 * Shared by the coupon and by the host steps that render its live region.
 *
 * The green is pulled toward an INK chosen from the widget BACKGROUND (black
 * on a light theme, white on a dark one), not toward the text colour: mixing
 * toward `--yebo-text` failed AA whenever a venue's text was mid-grey (#666 on
 * white ⇒ 3.6:1). 40% green over that ink measures ≥ 8:1 on white, cream and
 * the default dark theme. Engines without relative colour syntax fall back to
 * the previous text-colour mix. `--booking-deal-color` still overrides it all.
 * Meaning is carried by the words ("applied"), never by the colour alone.
 */
export const couponStatusStyles = css`
  :host {
    --yebo-deal-ink: var(--yebo-text);
    --yebo-deal-color: var(
      --booking-deal-color,
      color-mix(in srgb, var(--yebo-success) 40%, var(--yebo-deal-ink))
    );
  }

  @supports (color: oklch(from red l c h)) {
    :host {
      --yebo-deal-ink: oklch(from var(--yebo-bg) clamp(0, (0.57 - l) * 1000, 1) 0 0);
    }
  }

  .coupon-applied {
    margin-top: 8px;
    font-size: var(--yebo-font-size-sm);
    color: var(--yebo-text-muted);
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
  }

  .deal-applied {
    margin-top: 8px;
    font-size: var(--yebo-font-size-sm);
    font-weight: 600;
    color: var(--yebo-deal-color);
  }

  .deal-hint {
    margin-top: 8px;
    font-size: var(--yebo-font-size-sm);
    color: var(--yebo-text-muted);
  }

  .coupon-error {
    margin-top: 8px;
    font-size: var(--yebo-font-size-sm);
    color: var(--yebo-error);
  }

  .coupon-error-note {
    display: block;
    color: var(--yebo-text-muted);
  }
`;

@customElement('yebo-ticket-coupon')
export class YeboTicketCoupon extends LitElement {
  static styles = [
    themeTokens,
    resetStyles,
    baseStyles,
    couponStatusStyles,
    css`
      :host {
        display: block;
        margin-bottom: var(--yebo-space-md);
      }

      .coupon-title {
        font-size: var(--yebo-font-size-sm);
        font-weight: 600;
        color: var(--yebo-text-heading);
        margin-bottom: 8px;
      }

      .coupon-row {
        display: flex;
        gap: 8px;
        align-items: stretch;
      }

      .coupon-input {
        flex: 1;
        min-width: 0;
        padding: 10px 12px;
        border: 1px solid var(--yebo-border);
        border-radius: var(--yebo-radius);
        background: var(--yebo-bg);
        color: var(--yebo-text);
        font-size: var(--yebo-font-size-sm);
        text-transform: uppercase;
      }

      .coupon-apply,
      .coupon-remove {
        padding: 10px 16px;
        border: 1px solid var(--yebo-border);
        border-radius: var(--yebo-radius);
        background: var(--yebo-bg-elevated);
        color: var(--yebo-text-heading);
        font-size: var(--yebo-font-size-sm);
        font-weight: 600;
        cursor: pointer;
      }

      .coupon-apply[disabled] {
        opacity: 0.6;
        cursor: default;
      }

      .coupon-breakdown {
        margin-top: 12px;
        display: flex;
        flex-direction: column;
        gap: 6px;
      }

      .coupon-line {
        display: flex;
        justify-content: space-between;
        gap: var(--yebo-space-md);
        font-size: var(--yebo-font-size-sm);
        color: var(--yebo-text-muted);
      }

      /* #954 a11y L3 — raw --yebo-success is ~2.5:1 on white; reuse the
         background-derived deal colour (couponStatusStyles). */
      .coupon-discount-label,
      .coupon-discount-value {
        color: var(--yebo-deal-color);
        font-weight: 600;
      }

      .coupon-pool {
        margin-top: 4px;
        font-size: var(--yebo-font-size-sm);
        color: var(--yebo-text-muted);
      }

    `,
  ];

  /** What is IN the field. Survives a refusal so a typo can be corrected. */
  @property({ type: String, attribute: 'code' })
  code = '';

  /** The code the SERVER accepted ('' when none). */
  @property({ type: String, attribute: 'applied-code' })
  appliedCode = '';

  /** A LABEL only — the widget never multiplies by it. */
  @property({ type: Number, attribute: 'percent-off' })
  percentOff = 0;

  /** Server-computed pre-discount ticket total (cents). */
  @property({ type: Number, attribute: 'subtotal-cents' })
  subtotalCents = 0;

  /** Server-computed discount (cents). */
  @property({ type: Number, attribute: 'discount-cents' })
  discountCents = 0;

  @property({ type: String, attribute: 'currency' })
  currency = '';

  /** Remaining pool. −1 ⇒ the server sent nothing ⇒ UNKNOWN (no hint). */
  @property({ type: Number, attribute: 'tickets-remaining' })
  ticketsRemaining = -1;

  /** The SERVER's refusal sentence, rendered verbatim. */
  @property({ type: String, attribute: 'error' })
  error = '';

  @property({ type: Boolean, attribute: 'applying' })
  applying = false;

  /** #954 — the server's display label for an accepted TYPED code
   *  ("Buy 1 Get 1 Free", "10% off"). '' ⇒ fall back to the #816 percent copy. */
  @property({ type: String, attribute: 'applied-label' })
  appliedLabel = '';

  /** #954 — the quote said `autoApplied`: the event's deal is on this order. */
  @property({ type: Boolean, attribute: 'deal-applied' })
  dealApplied = false;

  /** #954 — the deal's code-free name, from the server. */
  @property({ type: String, attribute: 'deal-label' })
  dealLabel = '';

  /** #954 — why a live deal did not apply (for branching only; never shown). */
  @property({ type: String, attribute: 'deal-reason' })
  dealReason = '';

  /** #954 — the server's sentence for a deal that did not apply, verbatim. */
  @property({ type: String, attribute: 'deal-message' })
  dealMessage = '';

  /**
   * #954 a11y M1/M2 — the host step owns the live region (rendered from its
   * first paint) and fills it with `renderCouponStatusContent`. When set, this
   * element draws no status line and no refusal of its own: exactly one
   * speaker per message. Unset (standalone), it keeps its own region.
   */
  @property({ type: Boolean, attribute: 'external-status' })
  externalStatus = false;

  /** Live field text, so Apply sends what the guest can see. */
  @state()
  private _draft: string | null = null;

  /**
   * #954 a11y M3 (WCAG 2.4.3) — Apply and Remove are different template
   * branches, so the button the guest pressed is destroyed when the server
   * answers. Remember what THIS guest did here, and put focus back on the
   * matching control once the answer lands — never for a change the guest did
   * not make here (a host re-quote, a code dropped at checkout).
   */
  private _focusAfter: 'remove' | 'input' | null = null;

  private get _value(): string {
    return this._draft ?? this.code ?? '';
  }

  private get _statusInput(): CouponStatusInput {
    return {
      appliedCode: this.appliedCode,
      dealApplied: this.dealApplied,
      dealLabel: this.dealLabel,
      dealMessage: this.dealMessage,
      discountCents: this.discountCents,
      currency: this.currency,
      error: this.error,
    };
  }

  private _input(e: Event) {
    const raw = (e.target as HTMLInputElement).value;
    this._draft = raw;
    // "Clearing the field removes the discount": an emptied field drops the
    // coupon immediately (and re-quotes at full price) rather than leaving a
    // discount on screen that the guest has visibly just deleted.
    if (!raw.trim() && (this.appliedCode || this.error)) {
      this.dispatchEvent(new CustomEvent('coupon-clear', { bubbles: true, composed: true }));
    }
  }

  /** Codes are case-insensitive; showing them uppercase matches the server. */
  private _blur(e: Event) {
    const input = e.target as HTMLInputElement;
    const normalised = normalize(input.value);
    input.value = normalised;
    this._draft = normalised;
  }

  private _apply() {
    const code = normalize(this._value);
    if (!code) {
      this.dispatchEvent(new CustomEvent('coupon-clear', { bubbles: true, composed: true }));
      return;
    }
    this._draft = code;
    this._focusAfter = 'remove';
    this.dispatchEvent(new CustomEvent('coupon-apply', {
      detail: { code },
      bubbles: true,
      composed: true,
    }));
  }

  private _remove() {
    this._draft = '';
    this._focusAfter = 'input';
    this.dispatchEvent(new CustomEvent('coupon-clear', { bubbles: true, composed: true }));
  }

  private _keydown(e: KeyboardEvent) {
    if (e.key === 'Enter') {
      e.preventDefault();
      this._apply();
    }
  }

  /**
   * Focus is "lost" when the control the guest used was removed: nothing in
   * this shadow root is focused and the document fell back to <body>. If the
   * guest has since moved on (or is still in the input, which survives the
   * swap), nothing is moved.
   */
  private _focusLost(): boolean {
    try {
      const root = this.renderRoot as ShadowRoot | null | undefined;
      if (root?.activeElement) return false;
      const active = document.activeElement;
      return !active || active === document.body || active === document.documentElement;
    } catch {
      return false;
    }
  }

  protected updated(changed: PropertyValues): void {
    super.updated?.(changed);
    // A refusal answers the apply. The intent is spent either way, so it can
    // never fire on some later, unrelated acceptance. If focus was lost while
    // the button sat disabled ("Applying…"), it goes to the field, where the
    // refused code still is — a typo is corrected, not retyped.
    if (changed.has('error') && this.error && this._focusAfter) {
      const intent = this._focusAfter;
      this._focusAfter = null;
      if (intent === 'remove' && !this.appliedCode && this._focusLost()) {
        this._focusInside('input.coupon-input');
      }
      return;
    }
    if (!changed.has('appliedCode') || !this._focusAfter) return;
    const was = !!changed.get('appliedCode');
    const now = !!this.appliedCode;
    if (was === now) return;
    const intent = this._focusAfter;
    this._focusAfter = null;
    if (!this._focusLost()) return;
    const selector = now && intent === 'remove'
      ? 'button.coupon-remove'
      : !now && intent === 'input'
        ? 'input.coupon-input'
        : '';
    if (selector) this._focusInside(selector);
  }

  private _focusInside(selector: string): void {
    try {
      (this.renderRoot as ShadowRoot | null)
        ?.querySelector<HTMLElement>(selector)
        ?.focus?.();
    } catch {
      // Never let a focus nicety break the step.
    }
  }

  private _format(cents: number): string {
    return formatCouponMoney(cents, this.currency);
  }

  /** Only when the server SENT a number, and only when it is small. */
  private get _showPool(): boolean {
    return (
      Number.isFinite(this.ticketsRemaining) &&
      this.ticketsRemaining >= 0 &&
      this.ticketsRemaining <= COUPON_POOL_HINT_MAX
    );
  }

  /** The discount line's label: the typed code, else the deal, else generic. */
  private get _discountLabel(): string {
    if (this.appliedCode) {
      const detail = this.appliedLabel.trim()
        || (this.percentOff > 0 ? `${this.percentOff}% off` : '');
      return `Promo code ${this.appliedCode}${detail ? ` (${detail})` : ''}`;
    }
    if (dealOn(this._statusInput) && this.dealLabel.trim()) return this.dealLabel.trim();
    return 'Promo code';
  }

  /**
   * Standalone only (no `external-status`): this element's own polite region,
   * carrying the status line and the refusal. A hosted coupon draws neither —
   * the host's region, which predates the first quote, says them.
   */
  private _renderStatus() {
    if (this.externalStatus) return nothing;
    return html`<div class="deal-status" role="status" aria-live="polite">${renderCouponStatusContent(this._statusInput)}</div>`;
  }

  private _renderBreakdown() {
    if (!(this.discountCents > 0)) return nothing;
    const label = this._discountLabel;
    return html`
      <div class="coupon-breakdown">
        ${this.subtotalCents > 0 ? html`
          <div class="coupon-line">
            <span>Tickets</span>
            <span>${this._format(this.subtotalCents)}</span>
          </div>
        ` : nothing}
        <div class="coupon-line">
          <span class="coupon-discount-label">${label}</span>
          <span class="coupon-discount-value">−${this._format(this.discountCents)}</span>
        </div>
      </div>
    `;
  }

  render() {
    const applied = !!this.appliedCode;
    return html`
      <div class="coupon-title">Have a promo code?</div>
      <div class="coupon-row">
        <input
          class="coupon-input"
          type="text"
          name="promo-code"
          autocomplete="off"
          spellcheck="false"
          aria-label="Promo code"
          placeholder="Promo code"
          .value=${this._value}
          @input=${this._input}
          @blur=${this._blur}
          @keydown=${this._keydown}
        />
        ${applied
          ? html`<button type="button" class="coupon-remove" @click=${this._remove}>Remove</button>`
          : html`<button
              type="button"
              class="coupon-apply"
              ?disabled=${this.applying}
              @click=${this._apply}
            >${this.applying ? 'Applying…' : 'Apply'}</button>`}
      </div>

      ${this._renderBreakdown()}

      ${this._renderStatus()}

      ${applied && this._showPool ? html`
        <div class="coupon-pool">
          Only ${this.ticketsRemaining}
          ${this.ticketsRemaining === 1 ? 'discounted ticket' : 'discounted tickets'} left.
        </div>
      ` : nothing}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'yebo-ticket-coupon': YeboTicketCoupon;
  }
}
